import React, { useMemo, useState } from "react";
import {
	Switch,
	Drawer,
	DrawerOverlay,
	DrawerContent,
	DrawerHeader,
	DrawerBody,
	DrawerCloseButton,
} from "@chakra-ui/react";
import { FiEdit3 } from "react-icons/fi";
import { SelectWithLabel } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import type { SelectOption } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import { RichTextEditor } from "newui/components/ui/RichTextEditor";
import { useShortlistProjects } from "../../hooks/useShortlistProjects";
import type { ShortlistProject } from "../../api/shortlist-report-api";

interface ProjectAllocationPanelProps {
	includeProjectPage: boolean;
	projectDescription: string;
	projectId: number | null;
	projectVisibleFields: Record<string, boolean>;
	onIncludeProjectPageChange: (include: boolean) => void;
	onProjectChange: (projectId: number | null) => void;
	onProjectDescriptionChange: (description: string) => void;
	onProjectVisibleFieldsChange: (fields: Record<string, boolean>) => void;
}

const PROJECT_FIELD_LABELS: { description?: string; key: string; label: string; }[] = [
	{ key: "client_logo", label: "Client Logo Image" },
	{ key: "banner_image", label: "Project Banner Image" },
	{ key: "name", label: "Project Name" },
	{ key: "project_type", label: "Project Type", description: "Shown only if multiple types exist" },
	{ key: "sectors", label: "Sectors", description: "Listed A-Z" },
	{ key: "location", label: "Project Location" },
	{ key: "project_lead", label: "Project Lead", description: "Full name and email" },
	{ key: "description", label: "Project Description" },
];

const ProjectAllocationPanel: React.FC<ProjectAllocationPanelProps> = ({
	projectId,
	projectDescription,
	includeProjectPage,
	projectVisibleFields,
	onProjectChange,
	onProjectDescriptionChange,
	onIncludeProjectPageChange,
	onProjectVisibleFieldsChange,
}) => {
	const { data: projects, isLoading } = useShortlistProjects();
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	const projectOptions: SelectOption[] = useMemo(
		() =>
			(projects ?? []).map((p: ShortlistProject) => ({
				label: p.name,
				value: p.id,
			})),
		[projects]
	);

	const selectedProject = useMemo(
		() => projects?.find((p) => p.id === projectId) ?? null,
		[projects, projectId]
	);

	const selectedOption = useMemo(
		() =>
			projectId
				? projectOptions.find((o) => o.value === projectId) ?? null
				: null,
		[projectId, projectOptions]
	);

	const handleProjectSelect = (selected: SelectOption | SelectOption[] | null) => {
		if (!selected || Array.isArray(selected)) {
			onProjectChange(null);
			onProjectDescriptionChange("");
			return;
		}
		const project = projects?.find((p) => p.id === selected.value);
		onProjectChange(selected.value as number);
		onProjectDescriptionChange(project?.description ?? "");
		onIncludeProjectPageChange(true);
	};

	const handleFieldToggle = (fieldKey: string) => {
		onProjectVisibleFieldsChange({
			...projectVisibleFields,
			[fieldKey]: !projectVisibleFields[fieldKey],
		});
	};

	return (
		<div className="rounded-lg border border-gray-200 p-4">
			<h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-gray-700">
				Project Allocation
			</h3>
			<p className="mb-3 text-xs text-gray-500">
				Optionally allocate this report to a project for archival and reference.
			</p>

			<SelectWithLabel
				name="projectAllocation"
				label=""
				noLabel
				placeholder="Select a project (optional)..."
				options={projectOptions}
				value={selectedOption}
				onChange={handleProjectSelect}
				isLoading={isLoading}
				isClearable
			/>

			{selectedProject && (
				<div className="mt-4 space-y-3">
					{/* Project info summary */}
					<div className="rounded-md bg-gray-50 p-3 text-sm">
						<p className="font-medium text-gray-700">{selectedProject.name}</p>
						{selectedProject.location && (
							<p className="text-xs text-gray-500">{selectedProject.location}</p>
						)}
						{selectedProject.project_lead && (
							<p className="text-xs text-gray-500">
								Lead: {selectedProject.project_lead.name}
							</p>
						)}
						{selectedProject.sectors.length > 0 && (
							<p className="text-xs text-gray-500">
								Sectors: {selectedProject.sectors.map((s) => s.name).join(", ")}
							</p>
						)}
					</div>

					{/* Include Project Details page toggle */}
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium text-gray-700">
								Include Project Details page
							</p>
							<p className="text-xs text-gray-500">
								Adds a page after the cover with project information
							</p>
						</div>
						<Switch
							isChecked={includeProjectPage}
							onChange={(e) => onIncludeProjectPageChange(e.target.checked)}
							colorScheme="blue"
							size="md"
						/>
					</div>

					{/* Edit Project Page button — shown when include details is checked */}
					{includeProjectPage && (
						<button
							type="button"
							onClick={() => setIsDrawerOpen(true)}
							className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
						>
							<FiEdit3 className="h-4 w-4" />
							Edit Project Page
						</button>
					)}
				</div>
			)}

			{/* Edit Project Page Drawer */}
			<Drawer
				isOpen={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
				placement="right"
				size="md"
			>
				<DrawerOverlay />
				<DrawerContent>
					<DrawerCloseButton />
					<DrawerHeader borderBottomWidth="1px" fontSize="md">
						Edit Project Details Page
					</DrawerHeader>
					<DrawerBody py={5}>
						<div className="flex flex-col gap-5">
							{/* Field visibility toggles */}
							<div>
								<h4 className="mb-3 text-sm font-semibold text-gray-700">
									Fields to Display
								</h4>
								<p className="mb-3 text-xs text-gray-500">
									Choose which project fields appear on the details page.
								</p>
								<div className="flex flex-col gap-2">
									{PROJECT_FIELD_LABELS.map(({ key, label, description }) => (
										<div
											key={key}
											className="flex items-center justify-between rounded-md border border-gray-100 px-3 py-2"
										>
											<div>
												<p className="text-sm text-gray-700">{label}</p>
												{description && (
													<p className="text-xs text-gray-400">{description}</p>
												)}
											</div>
											<Switch
												isChecked={projectVisibleFields[key] !== false}
												onChange={() => handleFieldToggle(key)}
												colorScheme="blue"
												size="sm"
											/>
										</div>
									))}
								</div>
							</div>

							{/* Project description editor */}
							{projectVisibleFields.description !== false && (
								<div>
									<h4 className="mb-1 text-sm font-semibold text-gray-700">
										Project Description
									</h4>
									<p className="mb-2 text-xs text-gray-500">
										Edit the description for this report only — changes won&apos;t affect the project record.
									</p>
									<RichTextEditor
										value={projectDescription}
										onChange={onProjectDescriptionChange}
										placeholder="Describe the project context for this report..."
										minHeight="150px"
									/>
									<p className="mt-1 text-right text-xs text-gray-400">
										{projectDescription.replace(/<[^>]*>/g, "").length} / 2000 characters
									</p>
								</div>
							)}
						</div>
					</DrawerBody>
				</DrawerContent>
			</Drawer>
		</div>
	);
};

export default ProjectAllocationPanel;
